import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DesktopConfig, MfitConsentConfig } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { Desktop } from "../Desktop";
import { WindowTitlebar } from "../WindowTitlebar";
import { InfoRows, IpadFrame, type MfitDone, type MfitGuideFn } from "./shared";

const DESK_APPS = [
  { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  { id: "outlook", label: "Mail", icon: "outlook", opens: "outlook" },
  { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
  { id: "files", label: "File Explorer", icon: "files", opens: "files" },
];

/**
 * iPad Safari flow after Alex opens the shared document:
 *   viewer  -> a "SecureDocs" page asks for a Microsoft sign-in
 *   signin  -> the real-looking Microsoft account picker
 *   consent -> "Permissions requested" (Cancel / Accept) = the decision
 *   accepted-> "Document access granted. Loading preview…" freezes, then the
 *              screen zooms into the dialog (cut to the attacker's world)
 *   cancelled-> Cancel, then report it or close.
 * `replay` (what would have happened) offers only Accept.
 */
type Stage = "viewer" | "signin" | "consent" | "appinfo" | "accepted" | "cancelled" | "reported";

function MsMark() {
  return (
    <span className="mfit-ms" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

export function MfitConsent({
  config,
  onDone,
  onGuideChange,
  side,
  onStage,
  onRecord,
}: {
  config: MfitConsentConfig;
  onDone: (d: MfitDone) => void;
  onGuideChange?: MfitGuideFn;
  /** desktop frame: extra content drawn on the desktop next to the browser window. */
  side?: ReactNode;
  /** reports each stage change (the host may speak a line). */
  onStage?: (stage: string) => void;
  /** records an event without leaving the beat (app info opened). */
  onRecord?: (eventId: string, evidence?: string) => void;
}) {
  const [stage, setStage] = useState<Stage>(config.startAt ?? "viewer");
  const infoSeen = useRef(false);
  useEffect(() => {
    onStage?.(stage);
    if (stage === "appinfo" && config.appInfoEventId && !config.replay && !infoSeen.current) {
      infoSeen.current = true;
      onRecord?.(config.appInfoEventId, `Opened the ${config.consent.appName} app information`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  const appName = config.consent.appName;
  // the iPad course keeps its original evidence text and logo
  const ev = (desk: string, orig: string) => (config.frame === "desktop" ? desk : orig);
  const initials = ev(appName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase(), "SD");
  const [zoom, setZoom] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const doneRef = useRef(false);
  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };
  const guideKey = config.replay && config.guide?.[`${stage}_replay`] ? `${stage}_replay` : stage;
  useEffect(() => {
    onGuideChange?.(config.guide?.[guideKey] ?? null);
  }, [guideKey, config.guide, onGuideChange]);
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);

  useEffect(() => {
    if (stage !== "accepted") return;
    // The preview never loads: the spinner stops (the screen "freezes"), then
    // the view zooms into the dialog and cuts to the attacker.
    const t0 = window.setTimeout(() => setFrozen(true), 1300);
    const t1 = window.setTimeout(() => setZoom(true), 2600);
    const t2 = window.setTimeout(() => {
      audio.breachAlert();
    }, 3300);
    const t3 = window.setTimeout(
      () =>
        finish({
          key: "accept",
          eventId: config.replay ? undefined : config.acceptEventId,
          evidence: ev(`Accepted the ${appName} permission request`, "Accepted the SecureDocs permission request"),
          events: config.replay ? [] : (config.acceptExtraEventIds ?? []).map((eventId) => ({ eventId, evidence: `Accepted the ${appName} permission request` })),
        }),
      4600,
    );
    return () => [t0, t1, t2, t3].forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const c = config.consent;
  const url = stage === "signin" || stage === "consent" || stage === "appinfo" || stage === "accepted" ? "login.microsoftonline.com" : config.url;
  const page = (
        <div className="mfit-safari-page">
          {stage === "viewer" && (
            <div className="mfit-docview">
              <header className="mfit-docview-h">
                <b>{config.viewer.brand}</b>
                <span>Shared by {config.viewer.sharedBy}</span>
              </header>
              <div className="mfit-docview-body">
                <div className="mfit-docview-paper" aria-hidden="true">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <i key={i} style={{ width: `${55 + ((i * 23) % 40)}%` }} />
                  ))}
                </div>
                <div className="mfit-docview-card">
                  <div className="mfit-docview-file">{config.viewer.fileName}</div>
                  <p>{config.viewer.body}</p>
                  <button type="button" className="mfit-btn mfit-btn--ms cf-pulse-target" onClick={() => setStage("signin")}>
                    <MsMark /> {config.viewer.signInLabel}
                  </button>
                  {(config.viewer.altLabels ?? []).map((l) => (
                    <button key={l} type="button" className="mfit-btn mfit-btn--ghost" disabled>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {stage === "signin" && (
            <div className="mfit-mspage">
              <div className="mfit-mscard">
                <div className="mfit-ms-brand">
                  <MsMark /> Microsoft
                </div>
                <h3>{config.signin.heading ?? "Pick an account"}</h3>
                {config.signin.pickHint && <p className="mfit-ms-sub">{config.signin.pickHint}</p>}
                <button type="button" className="mfit-ms-acct cf-pulse-target" onClick={() => setStage("consent")}>
                  <span className="mfit-ms-acct-av">{config.signin.name.slice(0, 1)}</span>
                  <span>
                    <b>{config.signin.name}</b>
                    <small>{config.signin.account}</small>
                    <em>Signed in</em>
                  </span>
                </button>
                <span className="mfit-ms-other">Use another account</span>
              </div>
            </div>
          )}

          {(stage === "consent" || stage === "appinfo" || stage === "accepted") && (
            <div className="mfit-mspage">
              <div className="mfit-mscard mfit-consent">
                <div className="mfit-ms-brand">
                  <MsMark /> Microsoft
                </div>
                <div className="mfit-ms-acctline">{config.signin.account}</div>
                <h3>Permissions requested</h3>
                <div className="mfit-consent-app">
                  <span className="mfit-consent-logo" aria-hidden="true">{initials}</span>
                  <span>
                    <b>{c.appName}</b>
                    <small>
                      {c.publisher}
                      {c.unverifiedLabel ? <em className="mfit-unver"> · {c.unverifiedLabel}</em> : null}
                    </small>
                  </span>
                </div>
                {stage === "appinfo" && c.appInfo ? (
                  <div className="mfit-consent-info">
                    <b>{c.appInfo.title}</b>
                    <InfoRows rows={c.appInfo.rows} />
                    {c.appInfo.note && <p>{c.appInfo.note}</p>}
                    <button type="button" className="mfit-btn" onClick={() => setStage("consent")}>
                      Back to the permissions
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="mfit-consent-intro">{c.intro}</p>
                    <ul className="mfit-perms">
                      {c.permissions.map((p) => (
                        <li key={p.title}>
                          <span className="mfit-perm-ico" aria-hidden="true" />
                          <span>
                            <b>{p.title}</b>
                            <small>{p.detail}</small>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="mfit-consent-foot">{c.footer}</p>
                    {c.appInfo && (
                      <button type="button" className="mfit-linkbtn" onClick={() => setStage("appinfo")}>
                        {c.appInfoLabel ?? "Show app info"}
                      </button>
                    )}
                    {stage === "accepted" ? (
                      <div className={`mfit-consent-load${frozen ? " is-frozen" : ""}`}>
                        <span className="mfit-spin" aria-hidden="true" />
                        {config.acceptedLine}
                      </div>
                    ) : (
                      <div className="mfit-consent-btns">
                        {!config.replay && (
                          <button
                            type="button"
                            className="mfit-btn mfit-btn--msgray"
                            onClick={() => {
                              audio.click();
                              setStage("cancelled");
                            }}
                          >
                            {c.cancelLabel}
                          </button>
                        )}
                        {!config.replay && config.reportOnConsent && (
                          <button
                            type="button"
                            className="mfit-btn mfit-btn--msgray"
                            onClick={() => {
                              audio.click();
                              setStage("reported");
                            }}
                          >
                            {config.cancel.reportLabel}
                          </button>
                        )}
                        <button
                          type="button"
                          className={`mfit-btn mfit-btn--msblue${config.replay ? " cf-pulse-target" : ""}`}
                          onClick={() => setStage("accepted")}
                        >
                          {c.acceptLabel}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {(stage === "cancelled" || stage === "reported") && (
            <div className="mfit-mspage">
              <div className="mfit-mscard">
                <h3>{stage === "reported" ? "Reported to IT Security" : config.cancel.title}</h3>
                <p className="mfit-ms-sub">{stage === "reported" ? config.cancel.reportedNote : config.cancel.body}</p>
                {stage === "cancelled" ? (
                  <div className="mfit-consent-btns is-stack">
                    <button type="button" className="mfit-btn mfit-btn--msblue" onClick={() => setStage("reported")}>
                      {config.cancel.reportLabel}
                    </button>
                    <button
                      type="button"
                      className="mfit-btn mfit-btn--msgray"
                      onClick={() => finish({ key: "cancel", eventId: config.cancel.cancelEventId, evidence: ev(`Cancelled the ${appName} permission request`, "Cancelled the permission request") })}
                    >
                      {config.cancel.closeLabel}
                    </button>
                  </div>
                ) : (
                  <div className="mfit-consent-btns">
                    <button
                      type="button"
                      className="mfit-btn mfit-btn--msblue cf-pulse-target"
                      onClick={() =>
                        finish({ key: "cancel", eventId: config.cancel.reportEventId, evidence: ev(`Cancelled and reported the ${appName} request`, "Cancelled and reported the SecureDocs request") })
                      }
                    >
                      Continue
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
  );

  if (config.frame === "desktop") {
    const deskConfig: DesktopConfig = { clock: config.clock, date: config.date, icons: DESK_APPS, taskbar: DESK_APPS };
    return (
      <Desktop config={deskConfig} className="mfit-cdesk" openAppIds={["browser"]} activeAppId="browser" onOpen={() => undefined}>
        <div className={`mfit-cdwin${zoom ? " is-zooming" : ""}`}>
          <WindowTitlebar title={config.tabTitle ?? "Browser"} />
          <div className="mfit-cdaddr">
            <span className="mfit-cdaddr-nav" aria-hidden="true">{"\u2039 \u203A \u21BB"}</span>
            <span className="mfit-cdaddr-url">
              <i aria-hidden="true">{"\u{1F512}"}</i>
              {url}
            </span>
          </div>
          {page}
        </div>
        {side}
      </Desktop>
    );
  }

  return (
    <IpadFrame expanded clock={config.clock} className={zoom ? "is-zooming" : ""}>
      <div className="mfit-safari">
        <div className="mfit-safari-bar">
          <span className="mfit-safari-nav" aria-hidden="true">‹ ›</span>
          <span className="mfit-safari-url">
            <i aria-hidden="true">🔒</i>
            {url}
          </span>
          <span className="mfit-safari-tabs" aria-hidden="true">⧉</span>
        </div>
        {page}
      </div>
    </IpadFrame>
  );
}
